import type { CategoryNode } from 'tradera/backend/data/category-data';
import type { CategoryFilterCategoryViewModel } from 'tradera/state/services/types/webapi-discover-generated';
import { classNames } from 'tradera/utils/class-names';
import styles from './search-input.module.scss';
import {
    DropdownIcon,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from 'tradera/components/dropdown-menu/dropdown-menu';
import { Divider } from 'tradera/components/divider/divider';
import { useTranslator } from 'tradera/lang/translate';
import { getCategoryIcon } from 'tradera/helpers/category-icon-helper';

type SearchCategoryProps = {
    handleCategoryChange: (categoryId: number) => void;
    selectedCategoryId: number;
    selectedCategory: CategoryNode | CategoryFilterCategoryViewModel;
    topLevelCategories: CategoryNode[];
    categoryPath: CategoryFilterCategoryViewModel[];
};

export const SearchCategory = ({
    handleCategoryChange,
    selectedCategoryId,
    selectedCategory,
    topLevelCategories,
    categoryPath
}: SearchCategoryProps) => {
    const { t } = useTranslator();

    if (!topLevelCategories) {
        return null;
    }

    const isCategorySelected = selectedCategoryId !== 0;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <div
                    className={classNames(
                        styles.categoryLabel,
                        styles.categoryColor,
                        'position-relative text-styled d-flex align-items-center h-100 cursor-pointer focus-visible-inner'
                    )}
                    role="menu"
                    tabIndex={0}>
                    <span
                        className={classNames(styles.categoryName, 'px-2', {
                            'text-dark': isCategorySelected
                        })}>
                        {isCategorySelected && (
                            <span className="mr-1">
                                {selectedCategory.name}
                            </span>
                        )}
                        <span className="px-1">
                            <DropdownIcon className="icon icon-xs align-middle" />
                        </span>
                    </span>
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent size={320} position="right">
                <div>
                    {categoryPath
                        .filter((category) => category.id !== 0)
                        .map((option) => {
                            return (
                                <DropdownMenuItem
                                    key={option.id}
                                    className="text-truncate"
                                    onSelect={() =>
                                        handleCategoryChange(option.id)
                                    }>
                                    <span className="text-truncate">
                                        {option.name}
                                    </span>
                                </DropdownMenuItem>
                            );
                        })}
                    <DropdownMenuItem onSelect={() => handleCategoryChange(0)}>
                        {t('searchSelect_allCategories')}
                    </DropdownMenuItem>
                </div>
                <Divider as="div" />
                {topLevelCategories.map((option) => {
                    return (
                        <DropdownMenuItem
                            key={option.id}
                            icon={getCategoryIcon(
                                option.id,
                                option.name,
                                'icon-md mr-2'
                            )}
                            className="text-truncate"
                            onSelect={() => handleCategoryChange(option.id)}>
                            <span className="text-truncate">{option.name}</span>
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
